import { FC, useMemo, useState } from "react";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Skeleton,
} from "@mui/material";
import __ from "src/utils/Translations";
import useBasePath from "src/utils/hooks/useBasePath";
import { useFetchEmployeeBalances } from "src/Pages/Company/Absences/api/employee.queries";
import { useGetAbsenceType } from "src/Pages/Company/Absences/api/absenceTypes.queries";
import { fetchManagedEmployees } from "src/Pages/Company/Absences/api/api";
import transformBalancesToAbsenceTypeOptions from "src/utils/Company/Absences/transformBalancesToAbsenceTypeOptions";
import BreadcrumbsMui from "src/Components/BreadcrumbsMui/BreadcrumbsMui";
import Title from "src/Pages/Company/Absences/Title/Title";
import Fieldset from "src/Pages/Company/Absences/Forms/Fieldset";
import AbsenceRequestForm, {
  INPUT_MAX_WIDTH,
} from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequestForm";
import EmployeesAutocomplete from "src/Pages/Company/Absences/AbsenceRequest/EmployeesAutocomplete/EmployeesAutocompleteState";
import { Loader } from "src/Components/Loader/Loader";
import ErrorDataFetching from "src/Components/Error/ErrorDataFetching";
import { IAbsenceType } from "src/Pages/Company/Absences/AbsenceType/AbsenceType.models";
import {
  IAbsenceRequestFormAbsenceType,
  IEmployeeDetails,
} from "./AbsenceRequest.models";

export const toFormAbsenceType = (
  data: IAbsenceType
): IAbsenceRequestFormAbsenceType => ({
  id: data.id,
  name: data.name,
  description: data.description,
  presenceType: data.presenceType,
  billingCycleType: data.billingCycleConfiguration?.type,
  approvers: data.approvers,
  configurationOptions: data.configurationOptions,
  customFieldsConfiguration: data.customFieldsConfiguration,
  submitLimitationFrom: data.submitLimitationFrom,
  submitLimitationTo: data.submitLimitationTo,
});

const AbsenceRequestCreate: FC = () => {
  const basePath = useBasePath();
  const [selectedEmployee, setSelectedEmployee] =
    useState<IEmployeeDetails | null>(null);

  const [absenceTypeId, setAbsenceTypeId] = useState<string | undefined>(
    undefined
  );

  const {
    data: absenceType,
    isFetching: isFetchingAbsenceType,
    isError: isErrorAbsenceType,
  } = useGetAbsenceType(absenceTypeId);

  const {
    data: balancesData,
    isFetching: isFetchingBalances,
    isError: isErrorBalances,
  } = useFetchEmployeeBalances(selectedEmployee?.employeeId);

  const absenceTypeOptions = useMemo(() => {
    if (!balancesData) return [];

    return transformBalancesToAbsenceTypeOptions(balancesData.balanceDetails);
  }, [balancesData]);

  const handleAbsenceTypeChange = (event: SelectChangeEvent) => {
    setAbsenceTypeId(event.target.value);
  };

  const handleEmployeeChange = (newValue: IEmployeeDetails | null) => {
    setSelectedEmployee(newValue);
  };

  if (isErrorAbsenceType || isErrorBalances) return <ErrorDataFetching />;

  return (
    <>
      <BreadcrumbsMui
        items={[
          {
            label: __("Nieobecności"),
            to: `${basePath}/absences`,
          },
          {
            label: __("Złóż wniosek za pracownika"),
          },
        ]}
      />
      <Title title={__("Złóż wniosek za pracownika")} />
      <Paper sx={{ p: "2rem" }} data-testid="absence-request-create">
        <Fieldset legend={__("Złożony za pracownika")}>
          <EmployeesAutocomplete
            name="employee"
            label={__("Imię i nazwisko")}
            queryKey="employee"
            queryFn={fetchManagedEmployees}
            value={selectedEmployee}
            onChange={handleEmployeeChange}
            required
            enabled
            maxWidth
          />
        </Fieldset>
        {selectedEmployee && (
          <Fieldset legend={__("Rodzaj wniosku")}>
            {isFetchingBalances ? (
              <Skeleton height="56px" />
            ) : (
              <FormControl fullWidth margin="normal">
                <InputLabel id="absence-type-label" required>
                  {__("Nazwa wniosku")}
                </InputLabel>
                <Select
                  label={__("Nazwa wniosku")}
                  labelId="absence-type-label"
                  required
                  onChange={handleAbsenceTypeChange}
                  sx={{
                    maxWidth: INPUT_MAX_WIDTH,
                  }}
                  data-testid="absence-request-create-field__name"
                >
                  {absenceTypeOptions.map((absenceType) => (
                    <MenuItem key={absenceType.id} value={absenceType.id}>
                      {absenceType.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Fieldset>
        )}
        {isFetchingAbsenceType && <Loader />}
        {selectedEmployee && absenceType && balancesData && (
          <AbsenceRequestForm
            key={absenceTypeId}
            absenceType={toFormAbsenceType(absenceType)}
            employeeId={selectedEmployee.employeeId}
            employeeDailyWorkingHours={selectedEmployee.dailyWorkingHours}
          />
        )}
      </Paper>
    </>
  );
};

export default AbsenceRequestCreate;
